import React, { useState, useEffect } from 'react'

import { FadeIn, Label, Loader, OrderListItem, CTALink, ToggleIcon } from '@pb/components'
import { getLabelFromStorage } from '@pb/utils'
import { useFeature } from '@optimizely/react-sdk'
import { useDispatch, useSelector } from 'react-redux'

import { ACCOUNT_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import { BNPL_PAY_KLARNA_ORDER_FROM_ONLINE_ORDER_DETAILS } from 'constants/optimizely'
import { ACCOUNT_TRACKING_EVENTS } from 'features/account/constants'
import { operationFetchOmnicreditOrderDetails } from 'features/account/hm-pay/duck/operations'
import useTrackAccountClickEvent from 'features/account/hooks/useTrackAccountClickEvent'
import selectors from 'features/account/purchases/duck/selectors'

import { operationFetchOnlineActiveOrders } from '../../duck/operations'
import { NormalizedOnlineOrder } from '../../duck/types'
import OrderError from '../OrderError'
import styles from './ActivePurchasesOverview.scss'

const ActivePurchasesOverview: React.FC = () => {
  const dispatch = useDispatch()
  const [showAllOrders, setShowAllOrders] = useState(false)

  const orders: NormalizedOnlineOrder[] = useSelector(selectors.getOnlineActiveOrders)
  const isOnlineActiveOrdersLoading = useSelector(selectors.getIsOnlineActiveOrdersLoading)
  const isOnlineActiveOrdersLoadingFailed = useSelector(selectors.getIsOnlineActiveOrdersFailed)
  const shouldFetchOnlineActiveOrders = useSelector(selectors.getShouldFetchOnlineActiveOrders)
  const isPayLaterPostPurchaseEnabled = useSelector(appSelectors.getIsPayLaterPostPurchaseEnabled)
  const [isPayKlarnaOrderEnabled] = useFeature(BNPL_PAY_KLARNA_ORDER_FROM_ONLINE_ORDER_DETAILS)
  const isUserAuthenticatedInPostPurchase = useSelector(appSelectors.getIsPostPurchaseAuthenticated)

  const trackEvent = useTrackAccountClickEvent()
  const trackActiveOrdersClick = trackEvent(ACCOUNT_TRACKING_EVENTS.onActiveOrdersClick)
  const onClickPayLaterOrder = (omnicreditOrderId: string): void => {
    trackActiveOrdersClick()
    // Fetch the Klarna Order
    if (
      isPayKlarnaOrderEnabled &&
      isPayLaterPostPurchaseEnabled &&
      isUserAuthenticatedInPostPurchase
    ) {
      dispatch(operationFetchOmnicreditOrderDetails(omnicreditOrderId))
    }
  }

  const initialOrdersCount = 2
  const maxOrdersCount = 4

  const onlineActiveOrders = orders.filter(
    (_, index) => (showAllOrders && index < maxOrdersCount) || index < initialOrdersCount,
  )

  const activeOrdersId = 'active-orders'
  const shouldShowLoadMoreButton = orders?.length > initialOrdersCount
  const loadMoreLabel = showAllOrders ? 'member.button.showless' : 'member.button.showall'
  const payLaterLabel = getLabelFromStorage('omnicredit.optin.title')

  const loadMoreOrdersOnClick = (): void => setShowAllOrders(!showAllOrders)

  useEffect(() => {
    if (shouldFetchOnlineActiveOrders) {
      dispatch(operationFetchOnlineActiveOrders({ start: 0, end: maxOrdersCount, active: true }))
    }
  }, [])

  if (isOnlineActiveOrdersLoading) {
    return <Loader data-testid="loader-active-orders" />
  }

  if (isOnlineActiveOrdersLoadingFailed) {
    return (
      <OrderError
        headline={getLabelFromStorage('account.digitalreceipts.sorry')}
        text={getLabelFromStorage('account.digitalreceipts.cannot.display.receipts')}
      />
    )
  }

  if (!onlineActiveOrders || onlineActiveOrders.length === 0) {
    return null
  }

  return (
    <div className={styles.ActivePurchasesOverviewContainer}>
      <ul id={activeOrdersId}>
        {onlineActiveOrders.map(
          ({
            orderId,
            date,
            totalAmount,
            statusDisplay,
            statusSubtext,
            itemNumber,
            isPayLater,
            klarnaOrderId,
          }) => (
            <OrderListItem
              className={styles.orderListItem}
              Tag="li"
              key={orderId}
              orderMetaData={getLabelFromStorage('commerce_filter_itemsText', {
                replacements: itemNumber,
              })}
              orderDate={date}
              orderAmount={totalAmount}
              orderStatusDisplay={statusDisplay}
              statusSubtext={statusSubtext}
              payLaterLabel={isPayLater ? payLaterLabel : undefined}
              orderDetailRoute={`${ACCOUNT_ROUTES.OnlineOrderDetails.path}/${orderId}`}
              onClick={
                isPayLater && klarnaOrderId
                  ? () => onClickPayLaterOrder(klarnaOrderId)
                  : trackActiveOrdersClick
              }
            />
          ),
        )}
      </ul>
      {shouldShowLoadMoreButton && (
        <FadeIn delay={75 * (onlineActiveOrders.length + 1)}>
          <CTALink
            ctaLink
            Tag="button"
            className={styles.loadMoreButton}
            size="medium"
            iconPosition="end"
            fullWidth
            onClick={loadMoreOrdersOnClick}
            disabled={isOnlineActiveOrdersLoading}
            shouldShowSpinner={isOnlineActiveOrdersLoading}
            iconElement={<ToggleIcon isExpanded={showAllOrders} className={styles.toggleIcon} />}
            aria-expanded={showAllOrders}
            aria-controls={activeOrdersId}
          >
            <Label copyKey={loadMoreLabel} />
          </CTALink>
        </FadeIn>
      )}
    </div>
  )
}

export default ActivePurchasesOverview
